import React, { useEffect, useState } from 'react';
import { CreditCard, FileText, ExternalLink, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { errorMessage, formatINR, formatDate } from '@/lib/utils';

// Venue admin: what this venue owes the platform and how it's collected.
// Customers pay the venue directly (Razorpay sub-merchant account); the
// platform's flat per-booking / per-plan fees are invoiced on the 1st and
// debited through the venue's UPI AutoPay / e-NACH mandate set up here.
const MANDATE_LABEL = {
  none: 'Not set up',
  pending_authorization: 'Waiting for authorisation',
  pending_confirmation: 'Confirming with bank',
  active: 'Active',
  paused: 'Paused',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

const INVOICE_TONE = { paid: 'success', debit_failed: 'danger', payment_link_sent: 'warning' };

export async function openInvoice(invoiceId) {
  // The invoice endpoint needs the auth header, so fetch it and open the
  // HTML from a blob rather than linking to the API URL directly.
  const { data } = await api.get(`/admin/billing/invoices/${invoiceId}/html`, { responseType: 'text' });
  const url = URL.createObjectURL(new Blob([data], { type: 'text/html' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function AdminBilling() {
  const [data, setData] = useState(null);
  const [method, setMethod] = useState('upi');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const { data: d } = await api.get('/admin/billing');
      setData(d);
    } catch (err) {
      toast({ title: 'Failed to load billing', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setupAutoPay = async () => {
    setBusy(true);
    try {
      const { data: setup } = await api.post('/admin/billing/mandate', { method });
      if (setup.mode === 'mock') {
        toast({ title: 'AutoPay active (demo)', description: 'No Razorpay keys configured — mandate simulated.', variant: 'success' });
        load();
        return;
      }
      if (!window.Razorpay) {
        toast({ title: 'Checkout unavailable', description: 'Razorpay script failed to load', variant: 'error' });
        return;
      }
      const rzp = new window.Razorpay({
        key: setup.key_id,
        order_id: setup.order_id,
        customer_id: setup.customer_id,
        recurring: '1',
        name: setup.name,
        description: setup.description,
        prefill: setup.prefill,
        handler: async (resp) => {
          try {
            const { data: result } = await api.post('/admin/billing/mandate/verify', resp);
            toast({
              title: result.mandate_status === 'active' ? 'AutoPay active' : 'AutoPay authorised',
              description: result.mandate_status === 'active' ? 'Monthly platform fees will be collected automatically.' : 'Your bank is confirming the mandate — this page updates once it does.',
              variant: 'success',
            });
          } catch (err) {
            toast({ title: 'Could not confirm AutoPay', description: errorMessage(err.response?.data?.detail), variant: 'error' });
          } finally {
            load();
          }
        },
        modal: { ondismiss: () => load() },
      });
      rzp.on('payment.failed', (r) => toast({ title: 'Authorisation failed', description: r?.error?.description || 'Please try again', variant: 'error' }));
      rzp.open();
    } catch (err) {
      toast({ title: 'AutoPay setup failed', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const viewInvoice = async (id) => {
    try {
      await openInvoice(id);
    } catch (err) {
      toast({ title: 'Could not open invoice', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    }
  };

  if (!data) return <div className="text-white/40 text-sm py-12 text-center">Loading billing…</div>;

  const mandateStatus = data.mandate?.mandate_status || 'none';
  const ready = data.online_payments?.ready;

  return (
    <div className="space-y-8" data-testid="admin-billing-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Venue</div>
        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Billing</h1>
        <p className="mt-2 text-sm text-white/50 max-w-2xl">
          Customers pay your venue directly. The platform's fee for each online payment is invoiced on the 1st of every
          month and collected through AutoPay.
        </p>
      </div>

      <div className={`border p-5 flex items-start gap-3 ${ready ? 'border-volt/30 bg-volt/5' : 'border-[#FFB800]/30 bg-[#FFB800]/5'}`}>
        {ready ? <CheckCircle2 size={18} className="text-volt mt-0.5" /> : <AlertTriangle size={18} className="text-[#FFB800] mt-0.5" />}
        <div className="text-sm">
          <div className="font-bold uppercase tracking-wide">{ready ? 'Online payments are on' : 'Online payments are off'}</div>
          {!ready && <div className="text-white/60 mt-1">{data.online_payments?.reason}</div>}
          {data.razorpay_account_id && (
            <div className="text-white/40 text-xs mt-1">
              Razorpay account {data.razorpay_account_id} · {(data.razorpay_account_status || 'created').replace(/_/g, ' ')}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-surface border border-white/10 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">AutoPay mandate</div>
            <Badge tone={mandateStatus === 'active' ? 'success' : mandateStatus === 'none' ? 'default' : 'warning'}>
              {MANDATE_LABEL[mandateStatus] || mandateStatus}
            </Badge>
          </div>
          {mandateStatus === 'active' ? (
            <p className="text-sm text-white/60">
              Up to {formatINR(data.mandate.max_amount)} per month via {data.mandate.method === 'emandate' ? 'e-NACH' : 'UPI AutoPay'}.
              Your bank notifies you 24 hours before each debit.
            </p>
          ) : (
            <>
              <p className="text-sm text-white/60">Approve a one-time mandate so monthly platform fees are collected automatically.</p>
              <div className="flex gap-2">
                {[['upi', 'UPI AutoPay'], ['emandate', 'e-NACH (bank)']].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMethod(value)}
                    className={`flex-1 border px-3 py-2 text-xs uppercase tracking-wide ${method === value ? 'border-volt text-volt' : 'border-white/15 text-white/60'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Button onClick={setupAutoPay} disabled={busy} className="w-full" data-testid="setup-autopay">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Set up AutoPay
              </Button>
              <p className="text-[11px] text-white/40">UPI AutoPay charges ₹1 to verify, refunded automatically.</p>
            </>
          )}
        </div>

        <div className="bg-surface border border-white/10 p-6 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Your platform fees</div>
          <div className="flex justify-between text-sm"><span>Per court booking</span><b>{formatINR(data.booking_fee)}</b></div>
          {data.plan_fees.map((p) => (
            <div key={p.id} className="flex justify-between text-sm">
              <span className="text-white/70">{p.name}</span><b>{formatINR(p.platform_fee || 0)}</b>
            </div>
          ))}
          <p className="text-[11px] text-white/40 pt-1">Charged per successful online payment. Not added to customer prices.</p>
        </div>

        <div className="bg-surface border border-white/10 p-6 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Not yet invoiced</div>
          <div className="font-heading text-4xl font-black">{formatINR(data.unbilled.total)}</div>
          <div className="text-xs text-white/50">{data.unbilled.count} online payment(s) since your last invoice</div>
        </div>
      </div>

      <div className="bg-surface-raised border border-white/10 p-6">
        <h3 className="font-heading font-bold text-xl uppercase mb-4">Invoices</h3>
        {data.invoices.length === 0 ? (
          <div className="text-white/40 text-sm">No invoices yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40 border-b border-white/10">
                  <th className="pb-3 pr-4">Invoice</th>
                  <th className="pb-3 pr-4">Period</th>
                  <th className="pb-3 pr-4">Total</th>
                  <th className="pb-3 pr-4">Due</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3" />
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-white/5">
                    <td className="py-3 pr-4 font-mono text-xs">{inv.number}</td>
                    <td className="py-3 pr-4">{inv.period}</td>
                    <td className="py-3 pr-4">{formatINR(inv.total)}</td>
                    <td className="py-3 pr-4">{formatDate(inv.due_at)}</td>
                    <td className="py-3 pr-4">
                      <Badge tone={INVOICE_TONE[inv.status] || 'default'}>{inv.status.replace(/_/g, ' ')}</Badge>
                      {inv.overdue && inv.status !== 'paid' ? <Badge tone="danger" className="ml-2">Overdue</Badge> : null}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <Button variant="outline" size="sm" onClick={() => viewInvoice(inv.id)}><FileText size={12} /> View</Button>
                      {inv.payment_link_url && inv.status !== 'paid' ? (
                        <a href={inv.payment_link_url} target="_blank" rel="noreferrer" className="ml-2 inline-flex">
                          <Button size="sm"><ExternalLink size={12} /> Pay</Button>
                        </a>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
